import apiService from "../../../../service/Api";

export interface StatusChangeUserRef {
  id: number;
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  profile_picture_url?: string | null;
}

export type StatusChangeRequestStatus = "pending" | "approved" | "declined";

export interface BookingStatusChangeRequest {
  id: number;
  booking: number;
  booking_label: string;
  requested_status: string;
  reason: string;
  requested_by: StatusChangeUserRef | null;
  approvers: StatusChangeUserRef[];
  status: StatusChangeRequestStatus;
  decided_by: StatusChangeUserRef | null;
  decision_reason: string;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
  // Booking context, so an approver can decide without opening the booking.
  current_status: string;
  service: string | null;
  service_date: string | null;
  venue_name: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  deceased_name: string | null;
  deceased_image: string | null;
  deceased_details: {
    date_of_birth: string | null;
    date_of_death: string | null;
    gender: string | null;
    religion: string | null;
    relationship_to_deceased: string | null;
  } | null;
  contact_details: {
    email: string | null;
    address: string | null;
    role: string | null;
  } | null;
}

interface PaginatedResponse<T> {
  results?: T[];
}

const unwrap = <T>(data: T[] | PaginatedResponse<T>): T[] =>
  Array.isArray(data) ? data : data.results ?? [];

export const createStatusChangeRequest = async (
  bookingId: number,
  requestedStatus: string,
  approverIds: number[],
  reason: string,
): Promise<BookingStatusChangeRequest> => {
  const res = await apiService.post<BookingStatusChangeRequest>(
    `/api/bookings/booking/${bookingId}/request-status-change/`,
    { requested_status: requestedStatus, approver_ids: approverIds, reason },
  );
  return res.data;
};

export const fetchBookingStatusChangeRequests = async (
  bookingId: number,
): Promise<BookingStatusChangeRequest[]> => {
  const res = await apiService.get<
    BookingStatusChangeRequest[] | PaginatedResponse<BookingStatusChangeRequest>
  >(`/api/bookings/status-change-requests/`, { booking: bookingId });
  return unwrap(res.data);
};

export const fetchMyPendingApprovals = async (): Promise<
  BookingStatusChangeRequest[]
> => {
  const res = await apiService.get<
    BookingStatusChangeRequest[] | PaginatedResponse<BookingStatusChangeRequest>
  >(`/api/bookings/status-change-requests/`, {
    status: "pending",
    mine: "true",
  });
  return unwrap(res.data);
};

export const approveStatusChangeRequest = async (
  requestId: number,
): Promise<BookingStatusChangeRequest> => {
  const res = await apiService.post<BookingStatusChangeRequest>(
    `/api/bookings/status-change-requests/${requestId}/approve/`,
  );
  return res.data;
};

export const declineStatusChangeRequest = async (
  requestId: number,
  reason?: string,
): Promise<BookingStatusChangeRequest> => {
  const res = await apiService.post<BookingStatusChangeRequest>(
    `/api/bookings/status-change-requests/${requestId}/decline/`,
    { reason: reason || "" },
  );
  return res.data;
};
